/**
 * When a distribution schedule sends, ported from the prototype's step 4 (`gen`, `occ`,
 * `endInfo`). Calendar dates are UTC-midnight Dates so the viewer's zone never leaks in; the
 * send time is applied in the schedule's own IANA zone through distributionTime.ts.
 * The mock backend answers POST /schedule-preview with `previewSchedule`.
 */
import { formatClockTime, formatMonthDay } from "./distributionFormat";
import { timezoneOption, toIsoWithOffset, utcOffsetMinutes, zonedWallTimeToInstant } from "./distributionTime";
import type { DistributionScheduleApi, MonthlyRuleApi, SchedulePreviewApi } from "./distributions.types";

const DAY_MS = 86_400_000;
/** Enough for ten years of daily sends; stops a bad rule from spinning forever. */
const MAX_STEPS = 4000;

export const parseIsoDate = (iso: string | null | undefined): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? "");
  return match ? new Date(Date.UTC(+match[1], +match[2] - 1, +match[3])) : null;
};

export const toIsoDate = (date: Date): string => date.toISOString().slice(0, 10);

const addDays = (date: Date, days: number): Date => new Date(date.getTime() + days * DAY_MS);
/** 0 = Monday … 6 = Sunday. */
const weekdayOf = (date: Date): number => (date.getUTCDay() + 6) % 7;
const daysInMonth = (year: number, month0: number): number => new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();

const dayIn = (year: number, month0: number, day: number | "last" | null): Date => {
  const last = daysInMonth(year, month0);
  const n = day === "last" ? last : Math.min(day ?? 1, last);
  return new Date(Date.UTC(year, month0, n));
};

/** The n-th (or last) date of the month that passes `matches`, scanning from the start or the end. */
const nthMatching = (year: number, month0: number, nth: number | "last", matches: (date: Date) => boolean): Date => {
  const last = daysInMonth(year, month0);
  const days = Array.from({ length: last }, (_, i) => new Date(Date.UTC(year, month0, i + 1))).filter(matches);
  return nth === "last" ? days[days.length - 1] : days[Math.min(nth, days.length) - 1];
};

/** "The first Tuesday", "the last business day" (Monday to Friday, no holiday calendar). */
export const monthlyRuleDate = (year: number, month0: number, rule: MonthlyRuleApi): Date =>
  rule.kind === "weekday"
    ? nthMatching(year, month0, rule.nth, (date) => weekdayOf(date) === rule.weekday)
    : nthMatching(year, month0, rule.nth, (date) => weekdayOf(date) < 5);

/** Every calendar date the rule fires on, from `starts_on`, ignoring the end rule. */
export function* scheduleDates(schedule: DistributionScheduleApi): Generator<Date> {
  const start = parseIsoDate(schedule.starts_on);
  if (!start) return;
  const every = Math.max(1, Math.floor(schedule.every || 1));
  if (schedule.kind === "once") {
    yield start;
    return;
  }
  if (schedule.kind === "daily") {
    for (let k = 0; k < MAX_STEPS; k++) yield addDays(start, k * every);
    return;
  }
  if (schedule.kind === "weekly") {
    const offsets = Array.from(new Set(schedule.weekdays)).filter((d) => d >= 0 && d <= 6).sort((a, b) => a - b);
    if (!offsets.length) return;
    const weekStart = addDays(start, -weekdayOf(start));
    for (let k = 0; k < MAX_STEPS; k += every) {
      for (const offset of offsets) {
        const date = addDays(weekStart, k * 7 + offset);
        if (date >= start) yield date;
      }
    }
    return;
  }
  const y0 = start.getUTCFullYear();
  const firstMonth =
    schedule.kind === "monthly"
      ? start.getUTCMonth()
      : Math.floor(start.getUTCMonth() / 3) * 3 + ((schedule.quarter_month ?? 1) - 1);
  const stepMonths = schedule.kind === "monthly" ? every : every * 3;
  for (let k = 0; k < MAX_STEPS; k++) {
    const total = firstMonth + k * stepMonths;
    const year = y0 + Math.floor(total / 12);
    const date = schedule.monthly_rule
      ? monthlyRuleDate(year, total % 12, schedule.monthly_rule)
      : dayIn(year, total % 12, schedule.day_of_month);
    if (date >= start) yield date;
  }
}

export interface ScheduleRunsOptions {
  /** Up to this many sends. */
  count: number;
  /** Sends already made (an active distribution's "after N"). */
  runsDone?: number;
  /** Sends before this instant are left out (defaults to none). */
  fromMs?: number;
}

const endRule = (schedule: DistributionScheduleApi) => ({
  endDate: schedule.ends.kind === "on" ? parseIsoDate(schedule.ends.date) : null,
  total: schedule.ends.kind === "after" ? schedule.ends.runs : Infinity,
});

/** Send instants (ms) honouring the end rule, at most `limit` of them. */
const sendInstants = (schedule: DistributionScheduleApi, limit: number, options: ScheduleRunsOptions): number[] => {
  const { endDate, total } = endRule(schedule);
  const remaining = schedule.kind === "once" ? 1 : total - (options.runsDone ?? 0);
  const out: number[] = [];
  let seen = 0;
  let steps = 0;
  // Step the generator by hand: the loop stops early, and es5 can't `for…of` a generator.
  const dates = scheduleDates(schedule);
  for (let next = dates.next(); !next.done; next = dates.next()) {
    const date = next.value;
    if (++steps > MAX_STEPS || out.length >= limit) break;
    if (endDate && date > endDate) break;
    if (seen >= remaining) break;
    const instant = zonedWallTimeToInstant(toIsoDate(date), schedule.at, schedule.timezone);
    if (options.fromMs !== undefined && instant < options.fromMs) continue;
    seen += 1;
    out.push(instant);
  }
  return out;
};

const utcClock = (instant: number): string => {
  const d = new Date(instant);
  return formatClockTime(`${d.getUTCHours()}:${d.getUTCMinutes()}`);
};

/** POST /schedule-preview, computed locally (the mock uses it; the UI can for instant feedback). */
export const previewSchedule = (
  schedule: DistributionScheduleApi,
  options: ScheduleRunsOptions,
): SchedulePreviewApi => {
  const count = Math.min(10, Math.max(1, options.count));
  const instants = sendInstants(schedule, count, options);
  const ends = schedule.kind !== "once" && schedule.ends.kind !== "never";
  const all = ends ? sendInstants(schedule, MAX_STEPS, options) : instants;
  const zone = timezoneOption(schedule.timezone);
  const clock = formatClockTime(schedule.at);
  const offsetChanges: SchedulePreviewApi["offset_changes"] = [];
  instants.forEach((instant, index) => {
    if (index === 0) return;
    const before = utcOffsetMinutes(instants[index - 1], schedule.timezone);
    const now = utcOffsetMinutes(instant, schedule.timezone);
    if (before === now) return;
    const previousUtc = utcClock(instants[index - 1]);
    offsetChanges.push({
      at: toIsoWithOffset(instant, schedule.timezone),
      note: `Clocks change before the ${formatMonthDay(new Date(instant).toISOString(), schedule.timezone)} send. It still goes out at ${clock} ${zone.word}, now ${utcClock(instant)} UTC instead of ${previousUtc} UTC.`,
    });
  });
  return {
    timezone: schedule.timezone,
    runs: instants.map((instant) => toIsoWithOffset(instant, schedule.timezone)),
    last_run: ends && all.length ? toIsoWithOffset(all[all.length - 1], schedule.timezone) : null,
    offset_changes: offsetChanges,
  };
};

/** How many sends the schedule makes in all; null when it never ends. */
export const totalSends = (schedule: DistributionScheduleApi, runsDone = 0): number | null => {
  if (schedule.kind === "once") return 1;
  if (schedule.ends.kind === "never") return null;
  return sendInstants(schedule, MAX_STEPS, { count: MAX_STEPS, runsDone }).length;
};

/** The next send after `fromMs`, as ISO with offset, or null when nothing is left. */
export const nextSendIso = (schedule: DistributionScheduleApi, fromMs: number, runsDone = 0): string | null => {
  const [next] = sendInstants(schedule, 1, { count: 1, runsDone, fromMs });
  return next === undefined ? null : toIsoWithOffset(next, schedule.timezone);
};
